
import React, { useEffect, useState } from 'react';

import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';

import api from 'services/api';
import { getCodEmpresa } from 'lib/functions';

// Organização dos campos por seção
const GRUPOS = {
  Identificação: [
    'EMPRESA',
    'FANTASIA',
    'ABREVIATURA',
    'SLOGAN',
    'CNPJ',
    'INSCRICAOESTADUAL',
    'IMUNICIPAL',
    'SUFRAMA',
    'CADASTUR',
  ],

  Endereço: [
    'RUA',
    'NRO',
    'COMPLEMENTO',
    'BAIRRO',
    'CEP',
    'CODCIDADE',
    'CXPOSTAL',
  ],

  Contato: [
    'FONE',
    'CELULAR',
    'FAX',
    'EMAIL',
    'SITE',
  ],

  Tributação: [
    'CNAEFISCAL',
    'CODREGIMETRIB',
    'ALIQUOTAISS',
    'ALIQUOTAICMS',
    'PERCRETPIS',
    'PERCRETCOFINS',
    'PERCRETCSLL',
    'PERCRETIRF',
    'PERCRETINSS',
    'TAF',
  ],

  Retenções: [
    'RETEMPIS',
    'RETEMCOFINS',
    'RETEMCSLL',
    'RETEMIRF',
    'RETEMINSS',
    'CALCRETSOMENTEPJ',
    'RETIRFVLMAIORDEZ',
    'REALIZALCTOENTREEMPRESAS',
  ],
};

// Rótulos personalizados exibidos na interface.
// Os nomes técnicos dos campos permanecem inalterados.
const LABELS = {
  // Identificação
  EMPRESA: 'Razão Social',
  FANTASIA: 'Nome Fantasia',
  ABREVIATURA: 'Abreviatura',
  SLOGAN: 'Slogan',
  CNPJ: 'CNPJ',
  INSCRICAOESTADUAL: 'Inscrição Estadual',
  IMUNICIPAL: 'Inscrição Municipal',
  SUFRAMA: 'SUFRAMA',
  CADASTUR: 'CADASTUR',

  // Endereço
  RUA: 'Logradouro',
  NRO: 'Número',
  COMPLEMENTO: 'Complemento',
  BAIRRO: 'Bairro',
  CEP: 'CEP',
  CODCIDADE: 'Cidade',
  CXPOSTAL: 'Caixa Postal',

  // Contato
  FONE: 'Telefone',
  CELULAR: 'Celular',
  FAX: 'Fax',
  EMAIL: 'E-mail',
  SITE: 'Site',

  // Tributação
  CNAEFISCAL: 'CNAE Fiscal',
  CODREGIMETRIB: 'Regime Tributário',
  ALIQUOTAISS: 'Alíquota ISS (%)',
  ALIQUOTAICMS: 'Alíquota ICMS (%)',
  PERCRETPIS: 'Retenção PIS (%)',
  PERCRETCOFINS: 'Retenção COFINS (%)',
  PERCRETCSLL: 'Retenção CSLL (%)',
  PERCRETIRF: 'Retenção IRRF (%)',
  PERCRETINSS: 'Retenção INSS (%)',
  TAF: 'Taxa de Administração Financeira',

  // Retenções
  RETEMPIS: 'Reter PIS',
  RETEMCOFINS: 'Reter COFINS',
  RETEMCSLL: 'Reter CSLL',
  RETEMIRF: 'Reter IRRF',
  RETEMINSS: 'Reter INSS',
  CALCRETSOMENTEPJ: 'Calcular retenções somente para PJ',
  RETIRFVLMAIORDEZ: 'Reter IRRF somente acima de R$ 10',
  REALIZALCTOENTREEMPRESAS: 'Permitir lançamentos entre empresas',
};

// Campos que utilizam checkbox (S/N)
const FLAGS = new Set([
  'RETEMPIS',
  'RETEMCOFINS',
  'RETEMCSLL',
  'RETEMIRF',
  'RETEMINSS',
  'CALCRETSOMENTEPJ',
  'RETIRFVLMAIORDEZ',
  'REALIZALCTOENTREEMPRESAS',
]);

export default function DadosEmpresa() {
  const [dados, setDados] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');

  const codempresa = getCodEmpresa() || 0;

  // Consulta os dados da empresa ativa
  useEffect(() => {
    let ativo = true;

    api
      .get('/front/configuracoes/dados-empresa', {
        params: { codempresa },
      })
      .then(({ data }) => {
        if (ativo) {
          setDados(data);
          setOriginal(data);
        }
      })
      .catch((e) => {
        if (ativo) {
          setErro(
            e.response?.data?.error ||
              'Falha ao consultar empresa.'
          );
        }
      })
      .finally(() => {
        if (ativo) {
          setLoading(false);
        }
      });

    return () => {
      ativo = false;
    };
  }, [codempresa]);

  // Atualiza um campo em memória
  const alterarCampo = (campo, valor) => {
    setDados((anterior) => ({
      ...anterior,
      [campo]: valor,
    }));
  };

  // Salva os dados da empresa
  const salvar = async () => {
    setSaving(true);
    setErro('');
    setMsg('');

    try {
      const camposPermitidos = Object.values(GRUPOS).flat();

      const camposParaSalvar = Object.fromEntries(
        camposPermitidos
          .filter((campo) =>
            Object.prototype.hasOwnProperty.call(dados, campo)
          )
          .map((campo) => [campo, dados[campo]])
      );

      await api.put('/front/configuracoes/dados-empresa', {
        codempresa,
        ...camposParaSalvar,
      });

      setOriginal({ ...dados });
      setMsg('Dados da empresa salvos com sucesso.');
    } catch (e) {
      setErro(
        e.response?.data?.error ||
          'Falha ao salvar empresa.'
      );
    } finally {
      setSaving(false);
    }
  };

  // Restaura os valores originais
  const cancelar = () => {
    setDados({ ...original });
    setErro('');
    setMsg('');
  };

  // Renderiza cada campo conforme seu tipo
  const renderizarCampo = (campo) => {
    const rotulo = LABELS[campo] || campo;

    if (FLAGS.has(campo)) {
      return (
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={
                String(dados[campo] || 'N')
                  .trim()
                  .toUpperCase() === 'S'
              }
              onChange={(e) => {
                alterarCampo(
                  campo,
                  e.target.checked ? 'S' : 'N'
                );
              }}
            />
          }
          label={rotulo}
        />
      );
    }

    return (
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        label={rotulo}
        value={dados[campo] ?? ''}
        onChange={(e) => {
          const valor = e.target.value.toLocaleUpperCase('pt-BR');
          alterarCampo(campo, valor);
        }}
      />
    );
  };

  return (
    <div
      style={{
        padding: 24,
        maxWidth: 1100,
      }}
    >
      <Typography variant="h5">
        Dados da empresa
      </Typography>

      <Typography color="textSecondary">
        Cadastro compartilhado da empresa ativa.
        Alterações podem afetar outros módulos.
      </Typography>

      {loading ? (
        <CircularProgress style={{ marginTop: 24 }} />
      ) : (
        Object.entries(GRUPOS).map(([titulo, campos]) => (
          <Card
            key={titulo}
            style={{ marginTop: 16 }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {titulo}
              </Typography>

              <Grid container spacing={2}>
                {campos.map((campo) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={campo}
                  >
                    {renderizarCampo(campo)}
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        ))
      )}

      {erro && (
        <Typography
          color="error"
          style={{ marginTop: 12 }}
        >
          {erro}
        </Typography>
      )}

      {msg && (
        <Typography
          color="primary"
          style={{ marginTop: 12 }}
        >
          {msg}
        </Typography>
      )}

      <div
        style={{
          display: 'flex',
          gap: 12,
          marginTop: 20,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          disabled={
            loading ||
            saving ||
            (!!erro && !Object.keys(dados).length)
          }
          onClick={salvar}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>

        <Button
          variant="outlined"
          disabled={saving || loading}
          onClick={cancelar}
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
}
